import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { capacitorPreferencesStorage } from "@/lib/preferences-storage";
import { getCurrentWeekStart } from "./themePreference";
import type { CookingStep, MenuPlan, Recipe } from "@/types/menu";

/**
 * Active menu store — the single source of truth for whichever MenuPlan
 * the user is currently looking at (loaded from /menu/preview or
 * /menu/generate). Cooking progress + shopping ticks live here too so
 * they survive navigation, page reloads, and app backgrounding.
 *
 * Phase 10 adds the per-recipe cooking flow: `recipeProgress` is a
 * `slug → { index, done }` map for the day-by-day mode, parallel to
 * the batch-mode `cookingIndex` / `doneSteps`. The two flows can
 * interleave (user batch-cooks Sunday, then tap-cooks Wednesday).
 */
interface RecipeProgress {
  index: number;
  done: number[];
}

interface MenuState {
  plan: MenuPlan | null;
  /** The UUID of the persisted menu (set after /menu/validate). */
  menuId: string | null;

  // ── Next week ────────────────────────────────────────────────────────
  nextWeekPlan: MenuPlan | null;
  nextWeekMenuId: string | null;

  // ── Unsaved premium meal swap (audit C1 / M14) ─────────────────────
  // Set when `plan` / `nextWeekPlan` holds a swapped draft that only the
  // explicit "Enregistrer le menu" may persist. Persisted, so the flag
  // survives reloads, and independent of `menuId` (which is null in dev /
  // without Supabase, where the old `menuId !== null` heuristic failed).
  pendingSwap: boolean;
  nextWeekPendingSwap: boolean;

  // ── Batch mode ──────────────────────────────────────────────────────
  // step index in plan.cooking_plan
  cookingIndex: number;
  // completed step orders
  doneSteps: number[];

  // ── Per-recipe mode (Phase 10) ──────────────────────────────────────
  recipeProgress: Record<string, RecipeProgress>;

  // checked shopping items (key = `${aisle}:${name}`)
  pickedItems: string[];

  // ── Hydration guard ────────────────────────────────────────────────
  // Set to true after Zustand persist middleware finishes rehydrating
  // from Capacitor Preferences / localStorage. Prevents useMenuPlan
  // from fetching from the API before the persisted plan is available,
  // which caused a race where the API plan could be set and then
  // overwritten by the stale persisted plan.
  _hydrated: boolean;

  setPlan: (plan: MenuPlan) => void;
  clearPlan: () => void;

  setNextWeekPlan: (plan: MenuPlan, id: string | null) => void;
  clearNextWeekPlan: () => void;

  // Batch-mode methods
  goToStep: (index: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  markStepDone: (order: number) => void;
  markStepUndone: (order: number) => void;

  // Recipe-mode methods (Phase 10)
  setRecipeSteps: (slug: string, steps: CookingStep[]) => void;
  goToRecipeStep: (slug: string, index: number) => void;
  nextRecipeStep: (slug: string) => void;
  prevRecipeStep: (slug: string) => void;
  markRecipeStepDone: (slug: string, order: number) => void;
  markRecipeStepUndone: (slug: string, order: number) => void;

  togglePicked: (key: string) => void;
  resetShopping: () => void;

  // ── Sharing (Phase 14) ───────────────────────────────────────────
  setMenuId: (id: string | null) => void;

  // ── Hydration guard ───────────────────────────────────────────────
  setHydrated: () => void;
}

function emptyProgress(): RecipeProgress {
  return { index: 0, done: [] };
}

function clampIndex(value: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(value, 0), length - 1);
}

function findRecipe(plan: MenuPlan | null, slug: string): Recipe | null {
  if (!plan) return null;
  for (const day of plan.days) {
    for (const r of Object.values(day.meals)) {
      if (r?.slug === slug) return r;
    }
  }
  return null;
}

export const useMenu = create<MenuState>()(
  persist(
    (set, get) => ({
      plan: null,
      menuId: null,
      nextWeekPlan: null,
      nextWeekMenuId: null,
      pendingSwap: false,
      nextWeekPendingSwap: false,
      cookingIndex: 0,
      doneSteps: [],
      recipeProgress: {},
      pickedItems: [],
      _hydrated: false,

      setPlan: (plan) =>
        set({
          plan,
          menuId: null,
          pendingSwap: false,
          cookingIndex: 0,
          doneSteps: [],
          recipeProgress: {},
          pickedItems: [],
        }),
      clearPlan: () =>
        set({
          plan: null,
          menuId: null,
          pendingSwap: false,
          cookingIndex: 0,
          doneSteps: [],
          recipeProgress: {},
          pickedItems: [],
        }),

      // ── Batch ─────────────────────────────────────────────────────────
      goToStep: (index) => {
        const plan = get().plan;
        if (!plan) return;
        set({ cookingIndex: clampIndex(index, plan.cooking_plan.length) });
      },
      nextStep: () => {
        const { plan, cookingIndex } = get();
        if (!plan) return;
        set({
          cookingIndex: clampIndex(cookingIndex + 1, plan.cooking_plan.length),
        });
      },
      prevStep: () => {
        const { cookingIndex } = get();
        set({ cookingIndex: Math.max(cookingIndex - 1, 0) });
      },
      markStepDone: (order) =>
        set((s) =>
          s.doneSteps.includes(order)
            ? s
            : { doneSteps: [...s.doneSteps, order] },
        ),
      markStepUndone: (order) =>
        set((s) => ({ doneSteps: s.doneSteps.filter((n) => n !== order) })),

      // ── Per-recipe (Phase 10) ─────────────────────────────────────────
      setRecipeSteps: (slug, steps) =>
        set((s) => {
          if (!s.plan) return s;
          const days = s.plan.days.map((d) => ({
            ...d,
            meals: Object.fromEntries(
              Object.entries(d.meals).map(([slot, recipe]) =>
                recipe && recipe.slug === slug
                  ? [slot, { ...recipe, prep_steps: steps }]
                  : [slot, recipe],
              ),
            ),
          })) as MenuPlan["days"];
          return {
            plan: { ...s.plan, days },
            recipeProgress: {
              ...s.recipeProgress,
              [slug]: s.recipeProgress[slug] ?? emptyProgress(),
            },
          };
        }),
      goToRecipeStep: (slug, index) => {
        const recipe = findRecipe(get().plan, slug);
        if (!recipe) return;
        const len = recipe.prep_steps.length;
        set((s) => ({
          recipeProgress: {
            ...s.recipeProgress,
            [slug]: {
              ...(s.recipeProgress[slug] ?? emptyProgress()),
              index: clampIndex(index, len),
            },
          },
        }));
      },
      nextRecipeStep: (slug) => {
        const recipe = findRecipe(get().plan, slug);
        if (!recipe) return;
        const cur = get().recipeProgress[slug] ?? emptyProgress();
        set((s) => ({
          recipeProgress: {
            ...s.recipeProgress,
            [slug]: {
              ...cur,
              index: clampIndex(cur.index + 1, recipe.prep_steps.length),
            },
          },
        }));
      },
      prevRecipeStep: (slug) => {
        const cur = get().recipeProgress[slug] ?? emptyProgress();
        set((s) => ({
          recipeProgress: {
            ...s.recipeProgress,
            [slug]: { ...cur, index: Math.max(cur.index - 1, 0) },
          },
        }));
      },
      markRecipeStepDone: (slug, order) =>
        set((s) => {
          const cur = s.recipeProgress[slug] ?? emptyProgress();
          if (cur.done.includes(order)) return s;
          return {
            recipeProgress: {
              ...s.recipeProgress,
              [slug]: { ...cur, done: [...cur.done, order] },
            },
          };
        }),
      markRecipeStepUndone: (slug, order) =>
        set((s) => {
          const cur = s.recipeProgress[slug] ?? emptyProgress();
          return {
            recipeProgress: {
              ...s.recipeProgress,
              [slug]: { ...cur, done: cur.done.filter((n) => n !== order) },
            },
          };
        }),

      // ── Shopping ──────────────────────────────────────────────────────
      togglePicked: (key) =>
        set((s) =>
          s.pickedItems.includes(key)
            ? { pickedItems: s.pickedItems.filter((k) => k !== key) }
            : { pickedItems: [...s.pickedItems, key] },
        ),
      resetShopping: () => set({ pickedItems: [] }),

      // ── Next week ─────────────────────────────────────────────────────
      setNextWeekPlan: (plan, id) =>
        set({
          nextWeekPlan: plan,
          nextWeekMenuId: id,
          nextWeekPendingSwap: false,
        }),
      clearNextWeekPlan: () =>
        set({
          nextWeekPlan: null,
          nextWeekMenuId: null,
          nextWeekPendingSwap: false,
        }),

      // ── Sharing (Phase 14) ───────────────────────────────────────────
      setMenuId: (id) => set({ menuId: id }),

      // ── Hydration guard ───────────────────────────────────────────────
      setHydrated: () => set({ _hydrated: true }),
    }),
    {
      name: "bc.menu",
      version: 5,
      migrate: (persisted, version) => {
        const out = persisted as Record<string, unknown>;
        if (version < 3) out._hydrated = false;
        if (version < 4) {
          out.nextWeekPlan = null;
          out.nextWeekMenuId = null;
        }
        if (version < 5) {
          out.pendingSwap = false;
          out.nextWeekPendingSwap = false;
        }
        return out as unknown as MenuState;
      },
      onRehydrateStorage: () => (hydratedState) => {
        if (hydratedState) {
          // Monday transition: when the persisted plan belongs to a past week
          // and next week's plan is ready, promote it to the current plan.
          // Europe/Paris calendar, not the device's local/UTC mix.
          const currentMonday = getCurrentWeekStart();

          const planWeekStart = hydratedState.plan?.week_start;
          if (
            planWeekStart &&
            String(planWeekStart) < currentMonday &&
            hydratedState.nextWeekPlan
          ) {
            useMenu.setState({
              plan: hydratedState.nextWeekPlan,
              menuId: hydratedState.nextWeekMenuId,
              pendingSwap: hydratedState.nextWeekPendingSwap,
              nextWeekPlan: null,
              nextWeekMenuId: null,
              nextWeekPendingSwap: false,
              cookingIndex: 0,
              doneSteps: [],
              recipeProgress: {},
              pickedItems: [],
            });
          }
        }
        useMenu.getState().setHydrated();
      },
      storage: createJSONStorage(() => capacitorPreferencesStorage),
      partialize: (s) => ({
        plan: s.plan,
        menuId: s.menuId,
        nextWeekPlan: s.nextWeekPlan,
        nextWeekMenuId: s.nextWeekMenuId,
        pendingSwap: s.pendingSwap,
        nextWeekPendingSwap: s.nextWeekPendingSwap,
        cookingIndex: s.cookingIndex,
        doneSteps: s.doneSteps,
        recipeProgress: s.recipeProgress,
        pickedItems: s.pickedItems,
      }),
    },
  ),
);

// ── Selectors ─────────────────────────────────────────────────────────

export function findRecipeBySlug(
  plan: MenuPlan | null,
  slug: string,
): Recipe | null {
  return findRecipe(plan, slug);
}

export function shoppingKey(aisle: string, name: string): string {
  return `${aisle}:${name}`;
}
